import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Konva from 'konva';

import { Artwork } from '@definitions/artworks.types';
import {
  CANVAS_WIDTH,
  CANVAS_MARGIN,
  useArtworkSpaceViewCanvas,
  SpaceViewStep,
  CANVAS_MOBILE_MARGIN,
} from '@hooks/useArtworkSpaceView';
import { Direction, DistanceProps } from '@definitions/common.types';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { MOBILE, TABLET } from '@theme/media';
import { SIDEBAR_WIDTH } from '@components/blocks';
import { useWindowListener } from '@hooks/useWindowListener';
import { commonMessages } from '@components/common';

import { CanvasSpaceViewNoSSR } from './canvasSpaceViewNoSSR.component';
import { Canvas, CloseButton, Container, Content, Wrapper } from './artworkSpaceView.styles';

export interface ArtworkSpaceViewProps {
  toggleSpaceViewMode: () => void;
  setDistance: (data: DistanceProps) => void;
  artwork: Artwork;
  backgroundImage: string;
  measure: number;
  distance: DistanceProps;
  spaceViewStep: SpaceViewStep;
}

export const ArtworkSpaceView = forwardRef<Konva.Stage, ArtworkSpaceViewProps>(
  ({ toggleSpaceViewMode, artwork, backgroundImage, measure = 30, spaceViewStep, distance, setDistance }, ref) => {
    const artworkImage =
      process.env.NODE_ENV === 'development' ? artwork.primaryImage?.urlStandard : artwork.urlStandard;
    const imageRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const intl = useIntl();
    const [frame] = useState({ translate: [0, 0] });
    const { breakpoint } = useBreakpoint();
    const getCanvasMobileWidth = () =>
      breakpoint === MOBILE
        ? window.innerWidth - CANVAS_MOBILE_MARGIN
        : window.innerWidth - SIDEBAR_WIDTH - CANVAS_MARGIN;
    const [canvasWidth, setCanvasWidth] = useState(
      [TABLET, MOBILE].includes(breakpoint) ? getCanvasMobileWidth() : CANVAS_WIDTH
    );
    const [canvasHeight, setCanvasHeight] = useState(canvasWidth);
    const [imageRatio, setImageRatio] = useState(1);

    useArtworkSpaceViewCanvas(canvasRef.current, setDistance, spaceViewStep);
    const isHorizontalDistance = distance.direction === Direction.HORIZONTAL;

    const imageSize = useMemo(() => {
      const backgroundSize = isHorizontalDistance ? canvasWidth : canvasHeight;
      const distanceInch = measure;
      const imageInch = isHorizontalDistance ? artwork.unframedWidth : artwork.unframedHeight;
      const distancePercentage = (distance.value * 100) / backgroundSize;
      const backgroundInch = (distanceInch * 100) / distancePercentage;
      const size = Math.round((((imageInch * backgroundSize) / backgroundInch) * 100) / 100) || canvasWidth / 3;

      return isHorizontalDistance
        ? { width: size, height: size * imageRatio }
        : { height: size, width: size / imageRatio };
    }, [spaceViewStep, imageRatio]);

    useEffect(() => {
      const image = new Image();
      image.src = backgroundImage;
      image.onload = () => {
        const ratio = image.height / image.width;
        setCanvasHeight(canvasWidth * ratio);
      };
    }, [backgroundImage, canvasWidth]);

    useEffect(() => {
      if (artworkImage) {
        const image = new Image();
        image.src = artworkImage;
        image.onload = () => {
          const ratio = image.height / image.width;
          setImageRatio(ratio);
        };
      }
    }, [artworkImage]);

    useEffect(() => {
      if (spaceViewStep === SpaceViewStep.Second && imageRef.current) {
        imageRef.current.style.transform = 'translate(0px, 0px)';
        frame.translate = [0, 0];
      }
    }, [spaceViewStep]);

    const changeCanvasWidth = () => {
      if (breakpoint === TABLET) {
        setCanvasWidth(getCanvasMobileWidth());
      }
    };

    useWindowListener('resize', changeCanvasWidth, { throttle: 200 });

    return (
      <Wrapper>
        <Container style={{ width: canvasWidth, height: canvasHeight }}>
          <CloseButton onClick={toggleSpaceViewMode} aria-label={intl.formatMessage(commonMessages.closeLabel)} />
          <Content>
            <CanvasSpaceViewNoSSR
              width={canvasWidth}
              height={canvasHeight}
              backgroundImage={backgroundImage}
              spaceViewStep={spaceViewStep}
              imageSize={imageSize}
              artworkImage={artworkImage!}
              innerRef={ref}
            />
            <Canvas
              width={canvasWidth}
              height={canvasHeight}
              ref={canvasRef}
              show={spaceViewStep === SpaceViewStep.Second}
            />
          </Content>
        </Container>
      </Wrapper>
    );
  }
);
